import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { mergeMap, tap } from 'rxjs/operators'
import { ConfigService } from '../../../config/config.service'
import { Client } from '../../shared/client/client'
import { Audit, CommandeDetails, CommandeList, DeliveryInfos } from './commande'
import { Inventaire } from './inventaire'

@Injectable({
  providedIn: 'root'
})
export class CommandeService {


  constructor(
    private configService: ConfigService,
    private http: HttpClient
  ) { }

  public getCommandeList(clientId: string, page: number = 1, pageSize: number = 0, histo = false): Observable<any[]> {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<CommandeList[]>(config.apiPoint + config.getLHCommands.replace("{client}", clientId), {
          params: {
            page: page,
            limit: pageSize,
            histo: histo
          }
        })
      })
    )
  }

  public getLastCommand(clientId: string): Observable<any> {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<CommandeList>(config.apiPoint + config.getLastCommand.replace("{client}", clientId))
      })
    )
  }

  public getCommandeDetails(idCommande: string): Observable<CommandeDetails> {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<CommandeDetails>(config.apiPoint + config.getLHCommandDetails.replace("{commande}", idCommande))
      })
    )
  }

  public getCommandeAmadeus(idCommande: string): Observable<CommandeDetails> {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<CommandeDetails>(config.apiPoint + config.getAmadeusCommandDetails.replace("{commande}", idCommande))
      })
    )
  }

  public getClient(idCommande: string): Observable<Client> {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<Client>(config.apiPoint + config.getLHCommandClient.replace("{commande}", idCommande))
      })
    )
  }

  public updateCommandeDetails(commandeId: string, id: string, ligneCommandeId: string, article) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.put(
          config.apiPoint + config.putLHCommandDetails
            .replace("{line_id}", ligneCommandeId),
          article
        )
      })
    )
  }

  public sendCommande(id, currentClient: Client, lignes = []) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.post(
          config.apiPoint + config.postLHCommandSend
            .replace("{commande}", id),
          lignes
        )
      })
    )
  }

  countCommandeInprocess(client: Client) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<number>(config.apiPoint + config.getLHClientOrdersCount.replace("{client}", client.id))
      })
    )
  }

  public updateStatus(id, currentClient: Client) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.post(
          config.apiPoint + config.postLHCommandStatus
            .replace("{commande}", id),
          {}
        )
      })
    )
  }

  commandeDelivery(client: Client): Observable<DeliveryInfos> {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<DeliveryInfos>(config.apiPoint + config.getLHClientOrdersDelivery.replace("{client}", client.id))
      })
    )
  }

  audit(audit: Audit) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.patch(
          config.apiPoint + config.patchAudit,
          audit
        )
      })
    )
  }

  /**
   * Export PDF
   *
   * @param idCommande
   */
  public export(idCommande: string, idClient: string) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get(config.apiPoint + config.getExportCommand.replace("{commande}", idCommande), { responseType: 'arraybuffer' }).pipe(
          tap(res => {
            let blob = new Blob([res], { type: 'application/pdf' })
            let filename = `export_commande_${idCommande}_client_${idClient}.pdf`
            const nav = (window.navigator as any)
            if (nav.msSaveOrOpenBlob) {
              let newUrl = nav.msSaveOrOpenBlob(blob, filename)
            } else {
              let link = document.createElement('a')
              link.href = window.URL.createObjectURL(blob)
              link.setAttribute('download', filename)
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            }
          })
        )
      })
    )
  }

  public getFerie() {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<CommandeList[]>(config.apiPoint + config.getFerie)
      })
    )
  }

  public getInventairesCommande(commande) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<Inventaire[]>(config.apiPoint + config.getInventairesCommande.replace("{commande}", commande))
      })
    )
  }

  public getStatutInventaires() {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<any>(config.apiPoint + config.getStatutInventaires)
      })
    )
  }

  public postInventaire(inventaire) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.post<Inventaire>(config.apiPoint + config.postInventaire, inventaire)
      })
    )
  }

  public getInventaire(inventaire) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<Inventaire>(config.apiPoint + config.getInventaire.replace("{inventaire}", inventaire))
      })
    )
  }

  public validateInventaires(commande) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.post<any>(config.apiPoint + config.validateInventaires.replace("{commande}", commande), null)
      })
    )
  }

  public getClientDotationsJour(idClient: string, jour) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<any>(
          config.apiPoint + config.getClientDotationsJour
            .replace("{client}", idClient).replace("{jour}", jour)
        )
      })
    )
  }

  public getClientDotations(idClient: string) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<any>(
          config.apiPoint + config.getClientDotations
            .replace("{client}", idClient)
        )
      })
    )
  }

  public getClientHasDotations(idClient: string) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<any>(
          config.apiPoint + config.getClientHasDotations
            .replace("{client}", idClient)
        )
      })
    )
  }

  public saveDispatch(commandeId, infos) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.post<CommandeDetails>(config.apiPoint + config.saveDispatch.replace("{commande}", commandeId), infos)
      })
    )
  }

  public saveComplement(commandeId, infos) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.post<CommandeDetails>(config.apiPoint + config.saveComplement.replace("{commande}", commandeId), infos)
      })
    )
  }

  public saveBonLivraison(commandeId, infos) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.post<CommandeDetails>(config.apiPoint + config.saveBonLivraison.replace("{commande}", commandeId), infos)
      })
    )
  }

  public getClientStatsLH(idClient: string) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get<any>(
          config.apiPoint + config.getClientStatsLH
            .replace("{client}", idClient)
        )
      })
    )
  }

  public postClientStatsLH(idClient: string, stats) {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.post<any>(config.apiPoint + config.postClientStatsLH.replace("{client}", idClient), stats)
      })
    )
  }
}
